/**
 * Merge fields and blocked phrases for sequence steps.
 *
 * Fields: {{first_name}}, {{company}}, {{site}}, {{custom.<key>}}.
 * Rendering here is for the editor preview. The send path renders on the
 * executor side from the same field names.
 */

const FIELD_RE = /\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g

export const BASE_MERGE_FIELDS = ['first_name', 'company', 'site'] as const

export interface MergeContext {
  first_name?: string | null
  company?: string | null
  site?: string | null
  custom?: Record<string, string | number | string[] | null | undefined>
}

export interface RenderResult {
  text: string
  /** Fields that exist but have no value for this contact. */
  empty: string[]
  /** Fields that do not exist (typos, deleted custom fields). */
  unknown: string[]
}

export function extractMergeFields(text: string): string[] {
  const found = new Set<string>()
  for (const m of text.matchAll(FIELD_RE)) {
    if (m[1]) found.add(m[1].toLowerCase())
  }
  return [...found]
}

export function isKnownField(field: string, customKeys: readonly string[]): boolean {
  const f = field.toLowerCase()
  if ((BASE_MERGE_FIELDS as readonly string[]).includes(f)) return true
  if (f.startsWith('custom.')) return customKeys.includes(f.slice('custom.'.length))
  return false
}

export function unknownMergeFields(text: string, customKeys: readonly string[]): string[] {
  return extractMergeFields(text).filter((f) => !isKnownField(f, customKeys))
}

function asText(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}

export function renderTemplate(
  template: string,
  ctx: MergeContext,
  customKeys: readonly string[] = Object.keys(ctx.custom ?? {}),
): RenderResult {
  const empty = new Set<string>()
  const unknown = new Set<string>()
  const text = template.replace(FIELD_RE, (_all, raw: string) => {
    const f = raw.toLowerCase()
    if (!isKnownField(f, customKeys)) {
      unknown.add(f)
      return ''
    }
    let value = ''
    if (f === 'first_name') value = asText(ctx.first_name)
    else if (f === 'company') value = asText(ctx.company)
    else if (f === 'site') value = asText(ctx.site)
    else value = asText(ctx.custom?.[f.slice('custom.'.length)])
    if (value.trim() === '') empty.add(f)
    return value
  })
  return { text, empty: [...empty], unknown: [...unknown] }
}

/** Lower-case, trim, drop blanks and duplicates. */
export function normalizePhrases(phrases: readonly string[]): string[] {
  const out = new Set<string>()
  for (const p of phrases) {
    const t = p.trim().toLowerCase()
    if (t) out.add(t)
  }
  return [...out].sort()
}

/** Parse one-phrase-per-line (or comma separated) text from a textarea. */
export function parsePhraseList(text: string): string[] {
  return normalizePhrases(text.split(/[\n,]/))
}

/** Same rule as public.blocked_phrase_hits: case-insensitive substring match. */
export function findBlockedPhrases(text: string, phrases: readonly string[]): string[] {
  const hay = text.toLowerCase()
  return normalizePhrases(phrases).filter((p) => hay.includes(p))
}

export interface StepWarnings {
  blocked: string[]
  unknownFields: string[]
}

export function stepWarnings(
  step: { subject: string; body: string },
  phrases: readonly string[],
  customKeys: readonly string[],
): StepWarnings {
  const text = `${step.subject}\n${step.body}`
  return {
    blocked: findBlockedPhrases(text, phrases),
    unknownFields: unknownMergeFields(text, customKeys),
  }
}
